import { defineComponent as _defineComponent } from 'vue'
import {
	useCacheStore,
	useSettingsStore,
	useSolanaStore,
	useSwapStore,
	useWalletStore,
} from "@/pinia";
import {
	convertToHumanAmount,
	convertToTokenAmount,
	ERROR_MESSAGES,
	ISettingsPriorityLevel,
	IToken,
	SOL_MINT,
	SOL_TOKEN,
	USDC_MINT,
	USDC_TOKEN,
} from "@/shared";
import { computed, ref, watch } from "vue";
import { parseCustomError } from "@/api/token_swap/errors";
import TokenSelectNext from "@/components/token/TokenSelectNext.vue";
import store from "@/store";
import { createFluxBotSwap } from "@/api/endpoints/swap";
import { web3 } from "@project-serum/anchor";
import InputButtons from "../common/InputButtons.vue";
import { useAlertStore } from "@/pinia/alertStore";

// props
interface IProps {
	inMint: string;
	outMint: string;
	signAndSendTransaction?: Function;
	lastPrice: number;
}

interface IEmits {
	(e: "selectToken"): void;

	(e: "swap", sig: string): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'SwapNext',
  props: {
    inMint: null,
    outMint: null,
    signAndSendTransaction: null,
    lastPrice: null
  },
  emits: ["selectToken", "swap"],
  setup(__props: any, { emit }: { emit: ({
	(e: "selectToken"): void;

	(e: "swap", sig: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	inMint: string;
	outMint: string;
	signAndSendTransaction?: Function;
	lastPrice: number;
};



// computed
const priceUSD = computed<number>(() => {
	if (sell.value) {
		return props.lastPrice;
	}
	return cacheStore.getTokenPricesUSD(props.inMint)?.priceUSD;
});
const exceededBalance = computed<boolean>(() => {
	if (loading.value) return false;
	const amount = TokenSelectNextARef.value.price;
	const decimals = swapStore.getTokenA.decimals;
	return (
		Math.round(convertToTokenAmount({ amount, decimals })) >
		swapStore.getTokenABalance
	);
});
const emptyBalance = computed<boolean>(() => {
	return !swapStore.getTokenABalance;
});
const disabledSwap = computed<boolean>(() => {
	if (loading.value) return true;
	if (exceededBalance.value) return true;
	if (emptyBalance.value) return true;
	if (TokenSelectNextARef.value.price <= 0) return true;
	return false;
});
const isSlippageSelected = computed<(percent: number) => boolean>(
	() => (percent) => {
		return settingsStore.settings.slippage === percent;
	}
);
const isPriorityLevelSelected = computed<
	(level: ISettingsPriorityLevel) => boolean
>(() => (level) => {
	return settingsStore.settings.priorityLevel === level;
});

// store
const alertStore = useAlertStore();
const settingsStore = useSettingsStore();
const swapStore = useSwapStore();
const cacheStore = useCacheStore();
const solanaStore = useSolanaStore();
const walletStore = useWalletStore();

// data
const TokenSelectNextARef = ref<InstanceType<typeof TokenSelectNext> | null>(
	null
);
const loading = ref<boolean>(true);
const currentTokenSelected = ref<string>(null);
const sell = ref<boolean>(false);

function setSellMode(isSell: boolean): void {
	if (sell.value === isSell) return;

	sell.value = isSell;
	swapStore.swapAToB();
}

// methods
async function init({
	inMint,
	outMint,
}: {
	inMint: string;
	outMint: string;
}): Promise<void> {
	if (!walletStore.isConnected) return;
	if (!inMint || !outMint) return;

	reset();

	try {
		await Promise.all([
			updateTokenA(await getTokenByMint(inMint)),
			updateTokenB(await getTokenByMint(outMint)),
			fetchMintPriceUSD(inMint),
			fetchMintPriceUSD(outMint),
			swapStore.getTokenBalances(walletStore.address),
			swapStore.getPoolsDetail(walletStore.address),
		]);
	} catch (e: any) {
		alertStore.error("Error loading swap", e.message);
	} finally {
		loading.value = false;
	}
}

async function verifySwap(): Promise<void> {
	if (loading.value) return;

	// Track swap verification
	// @ts-ignore
	window.gtag("event", "swap_verification", {
		inMint: props.inMint,
		outMint: props.outMint,
		amount: TokenSelectNextARef.value.price,
		sellMode: sell.value
	});

	const id = Date.now();
	loading.value = true;

	try {
		await _doSwap(id);
		setTokenAmount(0);

		walletStore.refreshUserBalance();
	} catch (e: any) {
		store.commit("signatures/clear_status", id);
		console.error("Swap failed", e);
		alertStore.error(
			"Swap failed",
			parseCustomError(e.message || e).toString()
		);
		// @ts-ignore
		window.gtag("event", "swap_fail", {
			error: e,
			inMint: props.inMint,
			outMint: props.outMint,
			amount: TokenSelectNextARef.value.price,
			sellMode: sell.value
		});
	} finally {
		loading.value = false;
	}
}

async function _doSwap(id: number): Promise<void> {
	if (walletStore.isBotWallet)
		store.commit("signatures/add_signature", {
			sig: id,
			status: {},
		});

	let inMint = props.inMint;
	let outMint = props.outMint;

	if (sell.value) {
		inMint = props.outMint;
		outMint = props.inMint;
	}

	if (props.outMint === SOL_MINT) {
		inMint = USDC_MINT;
	}

	// Track swap initiated
	// @ts-ignore
	window.gtag("event", "swap_initiated", {
		id,
		inMint,
		outMint,
		amount: TokenSelectNextARef.value.price,
		sellMode: sell.value
	});

	const resp = await createFluxBotSwap({
		wallet: walletStore.address.toString(),
		inMint: inMint,
		outMint: outMint,
		inAmount: TokenSelectNextARef.value.price,
	}).catch(e => {
		store.commit("signatures/clear_status", id)
		// @ts-ignore
		window.gtag("event", "swap_bot_fail", {
			error: e,
			inMint,
			outMint,
			amount: TokenSelectNextARef.value.price
		});
		throw e
	});

	//Pass to txn polling
	if (resp.signature) {
		store.commit("signatures/update_status", {
			pendingSig: id,
			sig: resp.signature,
			status: {},
		});
		// @ts-ignore
		window.gtag("event", "swap_success", {
			signature: resp.signature,
			inMint,
			outMint,
			amount: TokenSelectNextARef.value.price,
			sellMode: sell.value
		});
		emit("swap", resp.signature);
		return;
	}

	if (!props.signAndSendTransaction) return;

	const transaction = web3.VersionedTransaction.deserialize(
		Buffer.from(resp.transaction, "base64")
	);

	console.log("Transaction", transaction);

	await props.signAndSendTransaction(
		transaction,
		"swap_trade",
		[],
		(sig:string) => {
			console.log("ONSWAP COMPLETE");
			// @ts-ignore
			window.gtag("event", "swap_success", {
				signature: sig,
				inMint,
				outMint,
				amount: TokenSelectNextARef.value.price,
				sellMode: sell.value
			});
			reset();
			emit("swap", sig);
		},
		store,
		null,
		alertStore
	);
}

async function getTokenByMint(mint: string): Promise<IToken> {
	let token = cacheStore.tokenList[mint];

	if (token) return token;

	token = await solanaStore.getToken(mint);

	cacheStore.addTokenList(token);

	return token;
}

async function fetchMintPriceUSD(mint?: string): Promise<void> {
	if (!mint) return;

	await cacheStore.fetchMintPriceUSD(mint);
}

async function updateTokenA(token: IToken): Promise<void> {
	swapStore.setTokenA(token);
}

async function updateTokenB(token: IToken): Promise<void> {
	swapStore.setTokenB(token);
}

async function setTokenAmount(amount: number) {
	updateTokenBAmount({ price: amount });
}

async function setTokenAmountPct(percent: number) {
	const balance = convertToHumanAmount({
		amount: swapStore.getTokenABalance,
		decimals: swapStore.getTokenA.decimals,
	});
	updateTokenBAmount({ price: balance * percent });
}

function updateTokenBAmount(data = { price: 0.0 }) {
	const { price } = data;
	TokenSelectNextARef.value.price = price;
}

function selectToken({ address }: any): void {
	const changeTokenA = currentTokenSelected.value === "A";

	const inMint = changeTokenA ? address : swapStore.getTokenA.mint;
	const outMint = changeTokenA ? swapStore.getTokenB.mint : address;

	init({
		inMint,
		outMint,
	});
}

function setSlippage(percent: number) {
	settingsStore.settings.slippage = percent;
}

function setPriorityLevel(level: ISettingsPriorityLevel): void {
	settingsStore.settings.priorityLevel = level;
}

function update(): void {
	const rpcURI = settingsStore.settings.customRpcUrl || store.state.rpc.rpcUrl;

	settingsStore.update();
	solanaStore.init(rpcURI);

	// legacy
	store.commit("settings/update", settingsStore.settings);
	store.commit("solana/set_endpoint", rpcURI);

	alertStore.success("Settings updated!");
}

function reset(): void {
	currentTokenSelected.value = null;
	swapStore.reset();
	loading.value = true;
}

// emits


// setup
init({
	inMint: props.inMint,
	outMint: props.outMint,
});

// watch
watch(
	() => settingsStore.getSettings,
	() => {
		update();
	},
	{ deep: true }
);

return { __sfc: true,props, priceUSD, exceededBalance, emptyBalance, disabledSwap, isSlippageSelected, isPriorityLevelSelected, alertStore, settingsStore, swapStore, cacheStore, solanaStore, walletStore, TokenSelectNextARef, loading, currentTokenSelected, sell, setSellMode, init, verifySwap, _doSwap, getTokenByMint, fetchMintPriceUSD, updateTokenA, updateTokenB, setTokenAmount, setTokenAmountPct, updateTokenBAmount, selectToken, setSlippage, setPriorityLevel, update, reset, emit, ERROR_MESSAGES, SOL_MINT, SOL_TOKEN, USDC_TOKEN, TokenSelectNext, InputButtons }
}

})